import { useState, useEffect } from "react";
import { fetchWeeklySqft } from "../../utils/dataReceiver";

/**
 * Fetch and manage state for the Weekly SQFT Completed modal.
 *
 * Fires once on mount. Returns an 8-week SQFT trend (oldest → newest) scoped
 * to the most-recent ISO weeks with data. The graph always contains exactly
 * 8 entries - the backend pads synthetic empty weeks when fewer than 8 weeks
 * of history exist, so chart rendering is always stable. Wk 8 is the current
 * ISO week and may carry sqft: 0 if the week has just started.
 *
 * Cancels the in-flight request automatically if the component unmounts before
 * the response arrives (AbortController cleanup).
 *
 * Cache TTL: 5 minutes - SQFT data updates at most once per day but the modal
 * may be opened repeatedly within a session.
 *
 * Note: weeklyTarget is sourced live from RTLS_ProductionGoals (PrimaryName =
 * 'sqftCompletion'). GoalValue is stored in thousands server-side
 * (e.g. 12 → 12 000 sqft/week) and is already expanded in the API response.
 *
 * @returns {{
 *   metrics: {
 *     sqftThisWeek: number,   - SQFT completed in the current ISO week
 *     weeklyTarget: number,   - goal from RTLS_ProductionGoals (e.g. 12000)
 *     eightWeekAvg: number,   - avg SQFT across non-zero weeks in the window
 *   },
 *   graph: Array<{
 *     week:  string,   - "Wk 1" … "Wk 8" (Wk 8 = most recent / current)
 *     label: string,   - "Apr 28" (Monday of that ISO week)
 *     sqft:  number,   - total SQFT completed that week (0 for empty weeks)
 *   }>,
 *   loading: boolean,
 *   error:   string | null,
 * }}
 */

let _cache = null;
let _cacheTime = 0;
const CACHE_TTL_MS = 5 * 60_000; // 5 minutes

const METRICS_DEFAULT = {
  sqftThisWeek: 0,
  weeklyTarget: 12000,
  eightWeekAvg: 0,
};

const useWeeklySqftData = () => {
  const [metrics, setMetrics] = useState(_cache?.metrics ?? METRICS_DEFAULT);
  const [graph, setGraph] = useState(_cache?.graph ?? []);
  const [loading, setLoading] = useState(!_cache);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const isStale = !_cache || Date.now() - _cacheTime > CACHE_TTL_MS;
    if (!isStale) return; // already fresh - skip fetch

    const load = async () => {
      try {
        const data = await fetchWeeklySqft(controller.signal);
        _cache = data;
        _cacheTime = Date.now();
        setMetrics(data.metrics);
        setGraph(data.graph);
      } catch (err) {
        if (err.name === "AbortError") return;
        setError(err.message ?? "Failed to load weekly SQFT data.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, []);

  return { metrics, graph, loading, error };
};

export default useWeeklySqftData;
